import React, { useState, useEffect, useCallback } from 'react';
import './StreamList.css';

// In production, REACT_APP_BACKEND_URL routes requests through the backend
// proxy (which has the whitelisted Hostinger IP). Falls back to direct API
// call in development when no backend is running.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = BACKEND_URL
  ? `${BACKEND_URL}/api/streams`
  : 'https://api.winner-stream.com/getLivescoreList.php';

const SPORT_ICONS = {
  baseball:   '⚾',
  basketball: '🏀',
  soccer:     '⚽',
  football:   '🏈',
  tennis:     '🎾',
  volleyball: '🏐',
  hockey:     '🏒',
  golf:       '⛳',
  boxing:     '🥊',
  mma:        '🥋',
  rugby:      '🏉',
  cricket:    '🏏',
  badminton:  '🏸',
  swimming:   '🏊',
  cycling:    '🚴',
  default:    '📺',
};

function getIcon(name) {
  return SPORT_ICONS[name] || SPORT_ICONS.default;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const today = new Date();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  if (dateStr === `${mm}-${dd}`) return '오늘';
  return dateStr;
}

export default function StreamList({ onSelect, selectedUrl }) {
  const [streams, setStreams]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [filter, setFilter]           = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStreams = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(API_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setStreams(Array.isArray(data) ? data : []);
        setLastUpdated(new Date());
        setLoading(false);
      })
      .catch(() => {
        setError('스트림 목록을 불러올 수 없습니다. API가 요청을 차단하고 있을 수 있습니다.');
        setLoading(false);
      });
  }, []);

  // Initial fetch + auto-refresh every 5 minutes
  useEffect(() => {
    fetchStreams();
    const interval = setInterval(fetchStreams, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStreams]);

  const liveCount = streams.filter((s) => s.status === 1).length;

  const filtered = streams.filter((s) => {
    if (filter === 'live')     return s.status === 1;
    if (filter === 'upcoming') return s.status === 0;
    return true;
  });

  // Live streams first within the list
  const sorted = [...filtered].sort((a, b) => b.status - a.status);

  return (
    <div className="stream-list">
      {/* Header */}
      <div className="stream-list__header">
        <div className="stream-list__title-row">
          <h3 className="stream-list__title">라이브 스트리밍</h3>
          {liveCount > 0 && (
            <span className="stream-list__live-count">
              <span className="pulse-dot" />
              {liveCount} 라이브
            </span>
          )}
        </div>
        <button
          className="stream-list__refresh"
          onClick={fetchStreams}
          title="새로고침"
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className={loading ? 'spin' : ''}>
            <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
          </svg>
        </button>
      </div>

      {/* Filters */}
      <div className="stream-list__filters">
        {['all', 'live', 'upcoming'].map((f) => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? 'filter-btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? '전체' : f === 'live' ? '● 라이브' : '◷ 예정'}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="stream-list__status">
          <div className="stream-list__spinner" />
          <span>스트림 로딩 중…</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="stream-list__error">
          <p>{error}</p>
          <button onClick={fetchStreams}>다시 시도</button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && sorted.length === 0 && (
        <div className="stream-list__empty">
          현재 {filter === 'live' ? '라이브' : filter === 'upcoming' ? '예정된' : ''} 스트림이 없습니다.
        </div>
      )}

      {/* Stream items */}
      {!loading && !error && sorted.length > 0 && (
        <ul className="stream-list__items">
          {sorted.map((stream, idx) => {
            const isLive       = stream.status === 1;
            const hasStream    = isLive && !!stream.videosUrl;
            const isSelected   = selectedUrl === stream.videosUrl && !!stream.videosUrl;

            return (
              <li
                key={idx}
                className={[
                  'stream-item',
                  isLive ? 'stream-item--live' : 'stream-item--upcoming',
                  isSelected ? 'stream-item--selected' : '',
                  hasStream ? 'stream-item--clickable' : '',
                ].join(' ')}
                onClick={() => hasStream && onSelect(stream)}
                role={hasStream ? 'button' : undefined}
                tabIndex={hasStream ? 0 : undefined}
                onKeyDown={(e) => e.key === 'Enter' && hasStream && onSelect(stream)}
              >
                <span className="stream-item__icon">{getIcon(stream.icon)}</span>

                <div className="stream-item__body">
                  <span className="stream-item__name">{stream.name}</span>
                  <span className="stream-item__desc">{stream.description}</span>
                </div>

                <div className="stream-item__right">
                  <span className="stream-item__date">
                    {formatDate(stream.schedule_date)}
                  </span>
                  <span className="stream-item__time">{stream.schedule_time}</span>
                  {isLive ? (
                    <span className="badge badge--live">라이브</span>
                  ) : (
                    <span className="badge badge--upcoming">예정</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Footer */}
      {lastUpdated && !loading && (
        <div className="stream-list__footer">
          업데이트 {lastUpdated.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
